import { Tooltip } from "@mantine/core";
import { OrderComment as OrderCommentType } from "@jasper/client-models";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCloudUpload,
  IconExclamationCircle,
  IconRefresh,
} from "@tabler/icons-react";
import { createStyles } from "@mantine/emotion";

const useStyles = createStyles(() => ({
  spin: {
    animation: "orderCommentSpin 1s linear infinite",
    transformOrigin: "center",
    "@keyframes orderCommentSpin": {
      from: { transform: "rotate(0deg)" },
      to: { transform: "rotate(360deg)" },
    },
  },
  pulse: {
    animation: "orderCommentPulse 1.2s ease-in-out infinite",
    transformOrigin: "center",
    "@keyframes orderCommentPulse": {
      "0%, 100%": { transform: "scale(1)", opacity: 1 },
      "50%": { transform: "scale(1.12)", opacity: 0.65 },
    },
  },
  jumpBounce: {
    animation: "orderCommentJumpBounce 900ms ease-out infinite",
    transformOrigin: "center",
    "@keyframes orderCommentJumpBounce": {
      "0%, 50%, 82%, 100%": { transform: "translateY(0)" },
      "28%": { transform: "translateY(-10px)" },
      "66%": { transform: "translateY(-4px)" },
    },
  },
}));

// themev2 tokens, falling back to the previous colors on themeV1 which does not define them
const ICON_COLORS = {
  pending: "var(--mantine-color-ink-4, gray)",
  error: "var(--mantine-color-error-5, red)",
  warning: "var(--mantine-color-warning-6, orange)",
  success: "var(--mantine-color-success-6, green)",
};

export const CommentIndicator = ({
  isUpdatingComment,
  isUploadingFiles,
  updatingCommentError,
  uploadingError,
  activeOrderComment,
}: {
  isUpdatingComment: boolean;
  isUploadingFiles: boolean;
  updatingCommentError?: string;
  uploadingError?: string;
  activeOrderComment?: OrderCommentType;
}) => {
  const { classes } = useStyles();

  if (!activeOrderComment) {
    return null;
  }

  if (isUpdatingComment) {
    return (
      <Tooltip label="Enregistrement du commentaire en cours">
        <IconRefresh
          style={{ color: ICON_COLORS.pending }}
          stroke={2}
          className={classes.spin}
        />
      </Tooltip>
    );
  }

  if (isUploadingFiles) {
    return (
      <Tooltip label="Téléversement des fichiers en cours">
        <IconCloudUpload
          style={{ color: ICON_COLORS.pending }}
          stroke={2}
          className={classes.pulse}
        />
      </Tooltip>
    );
  }

  if (updatingCommentError) {
    return (
      <Tooltip label="Erreur lors de l'enregistrement du commentaire">
        <IconExclamationCircle
          stroke={2}
          style={{ color: ICON_COLORS.error }}
          className={classes.jumpBounce}
        />
      </Tooltip>
    );
  }

  if (uploadingError) {
    return (
      <Tooltip label="Erreur lors du téléversement des fichiers">
        <IconAlertTriangle
          stroke={2}
          style={{ color: ICON_COLORS.warning }}
          className={classes.jumpBounce}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip label="Commentaire enregistré avec succès">
      <IconCircleCheck stroke={2} style={{ color: ICON_COLORS.success }} />
    </Tooltip>
  );
};
